import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { EASE } from "./motion";
import { EDITIONS, findEdition, productImage } from "../data/products";

export const EditionSwatch = ({ edition, active, onSelect, size = "md", light = false, testPrefix = "edition" }) => {
  const { lang } = useLanguage();
  const dim = size === "lg" ? "h-11 w-11 md:h-[68px] md:w-[68px]" : size === "sm" ? "h-9 w-9" : "h-11 w-11 md:h-14 md:w-14";
  const textActive = light ? "text-[#FAF8F5]" : "text-[#1C1C1A]";
  const textIdle = light ? "text-[#FAF8F5]/50" : "text-[#66625A]";
  return (
    <button
      type="button"
      data-testid={`${testPrefix}-${edition.code}`}
      aria-pressed={active}
      aria-label={edition.label[lang]}
      onClick={() => onSelect(edition.code)}
      className="group flex flex-col items-center gap-3"
    >
      <span className="relative flex items-center justify-center">
        <motion.span
          animate={{ scale: active ? 1 : 0.86, opacity: active ? 1 : 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className={`absolute -inset-[5px] rounded-full border ${light ? "border-[#FAF8F5]/70" : "border-[#1C1C1A]"}`}
        />
        <motion.span
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.95 }}
          className={`block ${dim} rounded-full shadow-[inset_0_1px_2px_rgba(255,255,255,0.35),0_6px_16px_-8px_rgba(0,0,0,0.35)] ring-1 ${light ? "ring-white/10" : "ring-black/10"}`}
          style={{ background: edition.swatch }}
        />
      </span>
      <span
        className={`font-mono text-[9px] uppercase tracking-[0.18em] transition-opacity duration-300 md:text-[10px] md:tracking-[0.25em] ${
          active ? textActive : `${textIdle} group-hover:opacity-100`
        }`}
      >
        {edition.name}
      </span>
    </button>
  );
};

export const EditionSelector = ({ value, onChange, size, light, testPrefix, className = "" }) => (
  <div className={`flex flex-wrap gap-4 md:gap-8 ${className}`} data-testid={`${testPrefix || "edition"}-selector`}>
    {EDITIONS.map((e) => (
      <EditionSwatch key={e.code} edition={e} active={value === e.code} onSelect={onChange} size={size} light={light} testPrefix={testPrefix} />
    ))}
  </div>
);

export const ProductStage = ({ product, edition, className = "", imgClassName = "", testId = "product-stage", children }) => {
  const ed = findEdition(edition);
  const src = productImage(product, ed.code);
  return (
    <motion.div
      data-testid={testId}
      data-edition={ed.code}
      animate={{ backgroundColor: ed.backdrop }}
      transition={{ duration: 0.9, ease: EASE }}
      className={`relative overflow-hidden ${className}`}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: ed.dark
            ? "radial-gradient(ellipse at 50% 45%, rgba(255,255,255,0.10), transparent 62%)"
            : "radial-gradient(ellipse at 50% 70%, rgba(0,0,0,0.05), transparent 65%)",
        }}
      />
      <AnimatePresence mode="wait" initial={false}>
        <motion.img
          key={src}
          src={src}
          alt={`${product.name} – ${ed.label.de}`}
          data-testid={`${testId}-image`}
          initial={{ opacity: 0, scale: 0.985, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 1.01, y: -6 }}
          transition={{ duration: 0.7, ease: EASE }}
          className={`relative z-[1] h-full w-full object-contain ${imgClassName}`}
        />
      </AnimatePresence>
      {children}
    </motion.div>
  );
};
